import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Users,
  LogIn,
  UserPlus,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/api/client";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Il ruolo a parole (titolare, amministratore, membro), non la chiave dell'API.
  const nomeRuolo = (ruolo) => t(`components.members.${ruolo}`, ruolo).toLowerCase();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [isAccepted, setIsAccepted] = useState(false);

  // Fetch invite info (public endpoint to show invite details)
  const { data: inviteInfo, isLoading: inviteLoading } = useQuery({
    queryKey: ["inviteInfo", token],
    queryFn: async () => {
      const response = await api.get(`/invites/${token}/info`);
      return response.data;
    },
    enabled: !!token && !authLoading,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/invites/${token}/accept`);
      return response.data;
    },
    onSuccess: (data) => {
      setIsAccepted(true);
      // Si entra subito nell'organizzazione appena raggiunta (la pagina si
      // ricarica per avere l'elenco aggiornato).
      try {
        if (data?.tenant?.id) localStorage.setItem("activeTenantId", data.tenant.id);
      } catch {
        // Senza localStorage resta l'organizzazione di prima: si cambia dal menu.
      }
      setTimeout(() => {
        window.location.href = "/home";
      }, 2000);
    },
  });

  useEffect(() => {
    // If authenticated, auto-accept on mount
    if (
      token &&
      isAuthenticated &&
      !isAccepted &&
      !acceptMutation.isLoading &&
      !acceptMutation.isError
    ) {
      acceptMutation.mutate();
    }
  }, [token, isAuthenticated, authLoading]);

  // Show loading while checking auth
  if (authLoading || inviteLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // If not authenticated, show login/register options
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>{t("invitations.youreInvited")}</CardTitle>
                <CardDescription>{t("invitations.joinTeam")}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {inviteInfo ? (
              <Alert>
                <Users className="h-4 w-4" />
                <AlertTitle>{t("invitations.invitationDetails")}</AlertTitle>
                <AlertDescription>
                  {t("invitations.invitedToJoin", {
                    tenant: inviteInfo.tenant_name,
                    role: nomeRuolo(inviteInfo.role),
                  })}
                  <br />
                  <br />
                  {t("invitations.loginToAccept")}
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t("invitations.invalidInvitation")}</AlertTitle>
                <AlertDescription>
                  {t("invitations.invalidInvitationDesc")}
                </AlertDescription>
              </Alert>
            )}

            {inviteInfo && (
              <div className="space-y-2">
                <Button
                  className="w-full"
                  onClick={() =>
                    navigate(`/login?returnUrl=/invites/${token}/accept`)
                  }
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  {t("invitations.loginToAcceptButton")}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() =>
                    navigate(
                      `/register?email=${encodeURIComponent(
                        inviteInfo.invited_email || ""
                      )}&returnUrl=/invites/${token}/accept`
                    )
                  }
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t("invitations.createAccountButton")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>{t("invitations.organizationInvitation")}</CardTitle>
              <CardDescription>{t("invitations.joinTeam")}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {acceptMutation.isLoading && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertTitle>{t("invitations.acceptingInvitation")}</AlertTitle>
              <AlertDescription>
                {t("invitations.acceptingInvitationDesc")}
              </AlertDescription>
            </Alert>
          )}

          {acceptMutation.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("invitations.acceptFailed")}</AlertTitle>
              <AlertDescription>
                {acceptMutation.error?.response?.data?.message ||
                  t("invitations.acceptFailedDesc")}
              </AlertDescription>
            </Alert>
          )}

          {acceptMutation.isSuccess && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-900">
                {t("invitations.invitationAccepted")}
              </AlertTitle>
              <AlertDescription className="text-green-800">
                {t("invitations.successfullyJoined", {
                  tenant: acceptMutation.data.tenant.name,
                  role: nomeRuolo(acceptMutation.data.role),
                })}
                <br />
                <br />
                {t("invitations.redirecting")}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            {acceptMutation.isError && (
              <>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate("/home")}
                >
                  {t("invitations.goToDashboard")}
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => acceptMutation.mutate()}
                >
                  {t("invitations.tryAgain")}
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
